import { useCallback, useMemo } from 'react';
import { useHistory } from 'react-router-dom';

import { ReactComponent as CoinbaseWalletImg } from ':cloud/assets/Products/Coinbase-Wallet.svg';
import { ReactComponent as WalletInfraImg } from ':cloud/assets/Products/Wallet-Infra.svg';
import { WAAS_DOCS } from ':cloud/utils/links';
import { openUrlInNewWindow } from ':cloud/utils/openUrlInNewWindow';
import { AppRoute } from ':cloud/utils/routes';
import { FULL_PAGE_MAX_WIDTH } from ':cloud/widgets/product/constants';
import { KeyFeatureItem } from ':cloud/widgets/product/KeyFeaturesSection';
import {
  BannerItem,
  ProductLandingPageTemplate,
} from ':cloud/widgets/product/ProductLandingTemplate';
import { SpecProps } from ':cloud/widgets/product/types';

function onPress(url: string) {
  return () => openUrlInNewWindow(url);
}

const specs: SpecProps[] = [
  {
    title: 'Latest SDK version',
    detail: 'v2.0.2',
  },
  {
    title: 'Latest client library version',
    detail: 'v1.0.8',
  },
  {
    title: 'Client library',
    detail: 'React Native',
  },
  {
    title: 'Pricing',
    detail: ' Starts at $0.12 per wallet',
  },
];

export function WaasLanding() {
  const history = useHistory();

  const handleCreateProject = useCallback(() => {
    history.push(AppRoute.Projects.New);
  }, [history]);

  const keyFeatures: KeyFeatureItem[] = useMemo(() => {
    return [
      {
        iconName: 'wallet',
        title: 'Seamless user experience',
        description:
          'Users can create, access and restore their wallets with authentication as simple as a username and password.',
      },
      {
        iconName: 'key',
        title: 'Own your keys',
        description:
          'Users have full control over their assets and can export their keys off platform at any time.',
      },
      {
        iconName: 'selfCustodyWallet',
        title: 'Native Integration',
        description:
          'With our APIs, you can build native wallet capabilities into your application while maintaining a cohesive user experience.',
      },
    ];
  }, []);

  const bannerItem1: BannerItem = useMemo(() => {
    return {
      title: 'Advanced cryptography',
      description:
        'Wallets deployed with our APIs use advanced multi-party computation (MPC) to keep user assets safe.',
      image: <CoinbaseWalletImg />,
      cta: 'Create',
      onPress: handleCreateProject,
    };
  }, [handleCreateProject]);

  const bannerItem2: BannerItem = useMemo(() => {
    return {
      title: 'Unique wallet infrastructure APIs',
      description:
        'Create and provide your customers with access to web3 wallets natively directly from your apps.',
      image: <WalletInfraImg />,
      cta: 'Docs',
      onPress: onPress(WAAS_DOCS),
      buttonEndIcon: 'externalLink',
    };
  }, []);

  return (
    <ProductLandingPageTemplate
      maxWidth={FULL_PAGE_MAX_WIDTH}
      bannerItem1={bannerItem1}
      bannerItem2={bannerItem2}
      keyFeatures={keyFeatures}
      specs={specs}
      title="Onboard your users to web3"
      description="Wallet as a Service (WaaS) offers a set of wallet infrastructure APIs that allows businesses to create and provide their customers access to web3 wallets natively in their apps."
      primaryCTA="Create"
      primaryOnPress={handleCreateProject}
      secondaryCTA="Docs"
      secondaryOnPress={onPress(WAAS_DOCS)}
    />
  );
}
